/**
 * RequireActiveProject — gate for project workspace routes (Research,
 * Participants, Interviews, …).
 *
 * A project's populated workspace pages are reachable only once
 * `projects.status === 'active'`. Non-active projects are redirected away —
 * but *where* depends on the cohort (spec 041 §R11):
 *   - **Early lifecycle** (`created` / `scoping` / `seeding`) now has a
 *     dedicated Overview landing, so a workspace deep-link redirects to
 *     `/projects/:shortId/overview` (its home), not the app root.
 *   - Any other non-active status (`planning` / `ready` / `decision_needed` /
 *     `completed` / `archived`) redirects to `/` as before.
 *
 * The Overview route itself is NOT wrapped in this gate — ProjectOverviewPage
 * branches on status internally (early-lifecycle landing vs OverviewLayout), so
 * early-lifecycle projects can land there without a redirect loop.
 *
 * Fail-open: only redirects once the project is loaded AND known to be
 * non-active. While loading or if the project can't be resolved, it renders the
 * children (the page handles its own loading / not-found) so an active project
 * never flashes a redirect on a slow status fetch.
 */

import type { ReactNode } from 'react';
import { Redirect } from 'wouter';

import { useProjectByShortId } from '@/hooks/v2/useProjectByShortId';
import { isEarlyLifecycleProjectStatus } from '@shared/realtime-types';

export interface RequireActiveProjectProps {
  shortId: string;
  children: ReactNode;
}

export function RequireActiveProject({ shortId, children }: RequireActiveProjectProps) {
  const project = useProjectByShortId(shortId);
  const status = (project.data as { status?: string } | null | undefined)?.status;

  // Only gate once we positively know the project is non-active.
  if (status && status !== 'active') {
    // Early-lifecycle projects have an Overview landing — send them there
    // instead of the app root, so a workspace deep-link degrades gracefully.
    if (isEarlyLifecycleProjectStatus(status)) {
      return <Redirect to={`/projects/${shortId}/overview`} />;
    }
    return <Redirect to="/" />;
  }

  return <>{children}</>;
}
